import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { SharedInfrastructure, SharedInfrastructureInsert, SharedInfrastructureUpdate } from '@/types/sharedInfrastructure';
import { toast } from 'sonner';

interface SharedInfrastructureState {
  infrastructures: SharedInfrastructure[];
  loading: boolean;

  loadInfrastructures: () => Promise<void>;
  addInfrastructure: (infrastructure: SharedInfrastructureInsert) => Promise<SharedInfrastructure | null>;
  updateInfrastructure: (id: string, updates: SharedInfrastructureUpdate) => Promise<void>;
  deleteInfrastructure: (id: string) => Promise<void>;
  getInfrastructureById: (id: string) => SharedInfrastructure | undefined;
  getInfrastructuresByZone: (zoneId: string) => SharedInfrastructure[];
}

export const useSharedInfrastructureStore = create<SharedInfrastructureState>()((set, get) => ({
  infrastructures: [],
  loading: false,

  loadInfrastructures: async () => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('shared_infrastructure')
        .select('*')
        .order('name');

      if (error) throw error;
      set({ infrastructures: (data || []) as SharedInfrastructure[] });
    } catch (error) {
      console.error('Error loading shared infrastructure:', error);
      toast.error('Failed to load shared infrastructure');
    } finally {
      set({ loading: false });
    }
  },

  addInfrastructure: async (infrastructure) => {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user?.id) {
        toast.error('User not authenticated');
        return null;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id')
        .eq('user_id', userData.user.id)
        .maybeSingle();

      if (!profile?.org_id) {
        toast.error('No organization found');
        return null;
      }

      const { data, error } = await supabase
        .from('shared_infrastructure')
        .insert({ ...infrastructure, org_id: profile.org_id })
        .select()
        .single();

      if (error) throw error;

      set(state => ({ infrastructures: [...state.infrastructures, data as SharedInfrastructure] }));
      toast.success('Shared infrastructure created');
      return data as SharedInfrastructure;
    } catch (error) {
      console.error('Error creating shared infrastructure:', error);
      toast.error('Failed to create shared infrastructure');
      return null;
    }
  },

  updateInfrastructure: async (id, updates) => {
    try {
      const { error } = await supabase
        .from('shared_infrastructure')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        infrastructures: state.infrastructures.map(i => i.id === id ? { ...i, ...updates } : i)
      }));
      toast.success('Shared infrastructure updated');
    } catch (error) {
      console.error('Error updating shared infrastructure:', error);
      toast.error('Failed to update shared infrastructure');
    }
  },

  deleteInfrastructure: async (id) => {
    try {
      const { error } = await supabase
        .from('shared_infrastructure')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        infrastructures: state.infrastructures.filter(i => i.id !== id)
      }));
      toast.success('Shared infrastructure deleted');
    } catch (error) {
      console.error('Error deleting shared infrastructure:', error);
      toast.error('Failed to delete shared infrastructure');
    }
  },

  getInfrastructureById: (id) => {
    return get().infrastructures.find(i => i.id === id);
  },

  getInfrastructuresByZone: (zoneId) => {
    return get().infrastructures.filter(i => i.zone_id === zoneId);
  },
}));
